import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { useInquiries } from '@/lib/query/hooks';
import {
  INQUIRY_CATEGORY_LABELS,
  INQUIRY_SOURCE_PRODUCT_LABELS,
  INQUIRY_STATUS_CHIP_COLORS,
  INQUIRY_STATUS_LABELS,
  formatInquiryDateTime,
  type InquiryCategory,
  type InquiryListItem,
  type InquirySortBy,
  type InquirySortOrder,
  type InquirySourceProduct,
  type InquiryStatus,
  type ListInquiriesParams,
} from '@/lib/types/mise/inquiry';

const STATUS_FILTER_VALUES: (InquiryStatus | '')[] = ['', 'open', 'closed'];

const CATEGORY_FILTER_VALUES: (InquiryCategory | '')[] = [
  '',
  'bug',
  'question',
  'feature_request',
  'data_issue',
  'other',
];

const SOURCE_PRODUCT_FILTER_VALUES: (InquirySourceProduct | '')[] = ['', 'mise', 'hub'];

const DEFAULT_SORT_BY: InquirySortBy = 'last_message_at';
const DEFAULT_SORT_ORDER: InquirySortOrder = 'desc';

function senderTypeLabel(type: 'user' | 'admin'): string {
  return type === 'admin' ? '運営' : 'ユーザー';
}

export function InquiriesPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [sourceProduct, setSourceProduct] = useState<InquirySourceProduct | ''>('');
  const [status, setStatus] = useState<InquiryStatus | ''>('');
  const [category, setCategory] = useState<InquiryCategory | ''>('');
  const [companyId, setCompanyId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [sellerId, setSellerId] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  // 一覧の初期表示件数。運営側オペレーションで一度に俯瞰したいケースが多く 100 を既定値にする。
  const [limit, setLimit] = useState(100);
  const [sortBy, setSortBy] = useState<InquirySortBy>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<InquirySortOrder>(DEFAULT_SORT_ORDER);

  const params = useMemo<ListInquiriesParams>(() => {
    const offset = page * limit;
    return {
      source_product: sourceProduct || undefined,
      status: status || undefined,
      category: category || undefined,
      company_id: companyId.trim() || undefined,
      account_id: accountId.trim() || undefined,
      seller_id: sellerId.trim() || undefined,
      q: query.trim() || undefined,
      offset,
      limit,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
  }, [
    sourceProduct,
    status,
    category,
    companyId,
    accountId,
    sellerId,
    query,
    page,
    limit,
    sortBy,
    sortOrder,
  ]);

  // 同じカラムを再クリックで方向トグル、別カラムは desc 起点。
  const handleSortChange = (column: InquirySortBy) => {
    setPage(0);
    setSortBy((prev) => {
      if (prev === column) {
        setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('desc');
      return column;
    });
  };

  const { data, isLoading, error } = useInquiries(params);

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" gap={2}>
        <CircularProgress />
        <Typography>認証状態を確認中...</Typography>
      </Box>
    );
  }
  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" gap={2}>
        <CircularProgress />
        <Typography>サインインページにリダイレクト中...</Typography>
      </Box>
    );
  }

  return (
    <AppLayout showSearch={false}>
      <Box sx={{ width: '95%', mx: 'auto', px: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          お問い合わせ管理
        </Typography>
        <FilterBar
          sourceProduct={sourceProduct}
          onSourceProductChange={(v) => {
            setSourceProduct(v);
            setPage(0);
          }}
          status={status}
          onStatusChange={(v) => {
            setStatus(v);
            setPage(0);
          }}
          category={category}
          onCategoryChange={(v) => {
            setCategory(v);
            setPage(0);
          }}
          companyId={companyId}
          onCompanyIdChange={(v) => {
            setCompanyId(v);
            setPage(0);
          }}
          accountId={accountId}
          onAccountIdChange={(v) => {
            setAccountId(v);
            setPage(0);
          }}
          sellerId={sellerId}
          onSellerIdChange={(v) => {
            setSellerId(v);
            setPage(0);
          }}
          query={query}
          onQueryChange={(v) => {
            setQuery(v);
            setPage(0);
          }}
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error instanceof Error ? error.message : 'お問い合わせ一覧の取得に失敗しました'}
          </Alert>
        ) : isLoading ? (
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <InquiryTable
            inquiries={data.inquiries}
            total={data.total}
            page={page}
            limit={limit}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            onPageChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(0);
            }}
            onRowClick={(id) => navigate(`/mise/inquiries/${id}`)}
          />
        ) : null}
      </Box>
    </AppLayout>
  );
}

interface FilterBarProps {
  sourceProduct: InquirySourceProduct | '';
  onSourceProductChange: (v: InquirySourceProduct | '') => void;
  status: InquiryStatus | '';
  onStatusChange: (v: InquiryStatus | '') => void;
  category: InquiryCategory | '';
  onCategoryChange: (v: InquiryCategory | '') => void;
  companyId: string;
  onCompanyIdChange: (v: string) => void;
  accountId: string;
  onAccountIdChange: (v: string) => void;
  sellerId: string;
  onSellerIdChange: (v: string) => void;
  query: string;
  onQueryChange: (v: string) => void;
}

function FilterBar(props: FilterBarProps) {
  return (
    <Paper sx={{ p: 2, mt: 2, mb: 2 }}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>プロダクト</InputLabel>
          <Select
            value={props.sourceProduct}
            label="プロダクト"
            onChange={(e) =>
              props.onSourceProductChange(e.target.value as InquirySourceProduct | '')
            }
          >
            {SOURCE_PRODUCT_FILTER_VALUES.map((v) => (
              <MenuItem key={v || 'all'} value={v}>
                {v ? INQUIRY_SOURCE_PRODUCT_LABELS[v as InquirySourceProduct] : 'すべて'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>ステータス</InputLabel>
          <Select
            value={props.status}
            label="ステータス"
            onChange={(e) => props.onStatusChange(e.target.value as InquiryStatus | '')}
          >
            {STATUS_FILTER_VALUES.map((v) => (
              <MenuItem key={v || 'all'} value={v}>
                {v ? INQUIRY_STATUS_LABELS[v as InquiryStatus] : 'すべて'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>カテゴリ</InputLabel>
          <Select
            value={props.category}
            label="カテゴリ"
            onChange={(e) => props.onCategoryChange(e.target.value as InquiryCategory | '')}
          >
            {CATEGORY_FILTER_VALUES.map((v) => (
              <MenuItem key={v || 'all'} value={v}>
                {v ? INQUIRY_CATEGORY_LABELS[v as InquiryCategory] : 'すべて'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="企業 ID"
          value={props.companyId}
          onChange={(e) => props.onCompanyIdChange(e.target.value)}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          label="アカウント ID"
          value={props.accountId}
          onChange={(e) => props.onAccountIdChange(e.target.value)}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          label="店舗名"
          placeholder="例: 0104, 札幌"
          value={props.sellerId}
          onChange={(e) => props.onSellerIdChange(e.target.value)}
          sx={{ minWidth: 200 }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          label="フリーテキスト検索 (件名 + 本文)"
          value={props.query}
          onChange={(e) => props.onQueryChange(e.target.value)}
          sx={{ minWidth: 280, flexGrow: 1 }}
        />
      </Stack>
    </Paper>
  );
}

interface InquiryTableProps {
  inquiries: InquiryListItem[];
  total: number;
  page: number;
  limit: number;
  sortBy: InquirySortBy;
  sortOrder: InquirySortOrder;
  onSortChange: (column: InquirySortBy) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onRowClick: (id: string) => void;
}

const SORTABLE_COLUMNS: { key: InquirySortBy; label: string }[] = [
  { key: 'source_product', label: 'プロダクト' },
  { key: 'company_id', label: '会社' },
  { key: 'seller_id', label: '店舗' },
  { key: 'user_name', label: '起票者' },
  { key: 'subject', label: '件名' },
  { key: 'category', label: 'カテゴリ' },
  { key: 'status', label: 'ステータス' },
  { key: 'last_message_at', label: '最終更新' },
  { key: 'last_message_sender_type', label: '最終発信者' },
];

function InquiryTable(props: InquiryTableProps) {
  return (
    <Paper sx={{ width: '100%' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1000, whiteSpace: 'nowrap' }} aria-label="お問い合わせテーブル">
          <TableHead>
            <TableRow>
              {SORTABLE_COLUMNS.map((column) => {
                const active = props.sortBy === column.key;
                return (
                  <TableCell key={column.key} sortDirection={active ? props.sortOrder : false}>
                    <TableSortLabel
                      active={active}
                      direction={active ? props.sortOrder : 'desc'}
                      onClick={() => props.onSortChange(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.inquiries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    お問い合わせはありません
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              props.inquiries.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => props.onRowClick(row.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{INQUIRY_SOURCE_PRODUCT_LABELS[row.source_product]}</TableCell>
                  <TableCell>{row.company_id}</TableCell>
                  <TableCell>{row.seller_name ?? row.seller_id ?? '-'}</TableCell>
                  <TableCell>{row.user_name ?? row.user_id}</TableCell>
                  <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.subject}
                  </TableCell>
                  <TableCell>{INQUIRY_CATEGORY_LABELS[row.category]}</TableCell>
                  <TableCell>
                    <Chip
                      label={INQUIRY_STATUS_LABELS[row.status]}
                      size="small"
                      color={INQUIRY_STATUS_CHIP_COLORS[row.status]}
                    />
                  </TableCell>
                  <TableCell>{formatInquiryDateTime(row.last_message_at)}</TableCell>
                  <TableCell>{senderTypeLabel(row.last_message_sender_type)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={props.total}
        page={props.page}
        onPageChange={(_, newPage) => props.onPageChange(newPage)}
        rowsPerPage={props.limit}
        onRowsPerPageChange={(e) => props.onLimitChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="表示件数"
      />
    </Paper>
  );
}
